import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  BaseInput,
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { moneyCentsParser } from "shared/components/forms/BaseInput/parsers";
import { Modal } from "shared/components/Modal";
import { useFormTyped } from "shared/hooks";
import { getFullName } from "shared/utils/get-full-name";
import { Applicant } from "types/application";
import { getApplicantRole } from "utils/getApplicantRole";
import { Banner } from "shared/components/Banner";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { getIncomeJobName, incomeTypeMap } from "../utils";
import { EditTransactionsDrawerData } from "../types";

type Props = Partial<EditTransactionsDrawerData> & {
  opened: boolean;
  onClose: () => void;
  applicant: Applicant;
  verificationId: number;
  applicationId: number;
  grossMonthlyIncomeDollars?: number;
  netMonthlyIncomeDollars?: number;
  hasNetMonthlyIncome?: boolean;
  isTotal?: boolean;
  onSuccess?: () => void;
};

export const EditIncomeModal = ({
  opened,
  onClose,
  applicant,
  verificationId,
  applicationId,
  grossMonthlyIncomeDollars,
  netMonthlyIncomeDollars,
  hasNetMonthlyIncome,
  isTotal,
  onSuccess,
  id: incomeId,
  type,
  additional_data
}: Props) => {
  const queryClient = useQueryClient();
  const form = useFormTyped({
    gross: (grossMonthlyIncomeDollars || 0) * 100,
    net: (netMonthlyIncomeDollars || 0) * 100
  });

  const mutation = useMutation({
    mutationKey: [
      "EDIT_INCOME",
      applicationId,
      verificationId,
      applicant.id,
      isTotal
    ],
    mutationFn: (data: { gross: number; net: number }) => {
      const endpoint = isTotal
        ? `portal/v1/applications/${applicationId}/verifications/${verificationId}/create_override?applicant_id=${applicant.id}`
        : `portal/v1/applications/${applicationId}/verifications/${verificationId}/create_override/income/${incomeId}?applicant_id=${applicant.id}`;
      return instance
        .post(endpoint, {
          gross_monthly_income: data.gross / 100,
          net_monthly_income: hasNetMonthlyIncome ? data.net / 100 : undefined
        })
        .then(({ data }) => {
          return data;
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[477px] w-full"
    >
      <form
        onSubmit={form.onSubmit({
          onSuccess: (values) => {
            mutation.mutate(
              {
                gross: values.gross,
                net: values.net
              },
              {
                onSuccess: () => {
                  onSuccess?.();
                  onClose();
                }
              }
            );
          }
        })}
      >
        <header className="px-6 py-5 text-lg font-bold flex justify-between items-center border-b border-alpha-100">
          Edit total verified income
          <IconButton
            HugeIcon={Cancel01Icon}
            size={IconButtonSize.SM}
            variant={IconButtonVariant.TEXT_BASE}
            onClick={onClose}
          />
        </header>
        <Banner
          variant="warning"
          textTheme="base"
          borders="bottom"
          body="Editing income amount may affect all summary metrics and decision
          recommendations."
          HugeIcon={
            <HugeiconsIcon
              icon={InformationCircleIcon}
              className="shrink-0 text-warning-500"
              size="20"
            />
          }
        />
        <div className="px-6 py-5 border-b border-alpha-100 flex items-center justify-between bg-white">
          <div className="space-y-1">
            <h2 className="text-base font-medium text-content-1000">
              {getFullName({
                firstName: applicant.first_name,
                lastName: applicant.last_name,
                middleName: applicant.middle_name,
                short: true
              })}
            </h2>
            <p className="text-sm text-content-400 font-medium">
              {getApplicantRole(applicant.role)}
            </p>
          </div>

          <div className="space-y-1 text-right">
            <h2 className="text-base font-medium text-content-1000">
              {getIncomeJobName(additional_data)}
            </h2>
            <p className="text-sm text-content-400 font-medium">
              {type && incomeTypeMap[type]}
            </p>
          </div>
        </div>

        <fieldset className="bg-background-900 p-6">
          <BaseInput
            id="gross"
            label="Verified gross monthly income"
            prefix="$"
            hideOptional
            parser={moneyCentsParser()}
            {...form.register("gross")}
          />
          <Show if={hasNetMonthlyIncome}>
            <div className="mt-2">
              <BaseInput
                id="net"
                label="Verified net monthly income"
                prefix="$"
                hideOptional
                parser={moneyCentsParser()}
                {...form.register("net")}
              />
            </div>
          </Show>
        </fieldset>

        <div className="px-6 py-4 flex gap-2 justify-end border-t border-alpha-100">
          <Button className="w-[125px]" onClick={onClose}>
            Cancel
          </Button>

          <Button
            className="w-[125px]"
            variant={ButtonVariant.PRIMARY}
            type="submit"
            loading={mutation.isPending}
          >
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};
